/**
 * Роли доступа: набор прав `crud` по каждому разделу админки.
 *
 * Любое право подразумевает чтение — иначе им нельзя воспользоваться. Снятие
 * `r` закрывает раздел целиком: он пропадает из меню. Системную роль
 * (супер-админ) не редактируют — её права зашиты.
 */
import { MODULES } from '../../app/modules'
import type { PermissionLetter } from '../rbac/permissions'
import type { Role } from './api'

export const PERMISSION_LETTERS: [PermissionLetter, string][] = [
  ['c', 'создание'],
  ['r', 'чтение'],
  ['u', 'изменение'],
  ['d', 'удаление'],
]

/** Порядок букв всегда один — «urc» и «cru» читались бы как разные права. */
export function normalize(letters: string): string {
  return PERMISSION_LETTERS.map(([letter]) => letter)
    .filter((letter) => letters.includes(letter))
    .join('')
}

export const rolePermission = (role: Role, module: string): string =>
  normalize(role.perms[module] ?? '')

/**
 * Названия разделов для сетки прав. Их больше, чем пунктов меню: журнал и
 * переводы своих экранов пока не имеют, но права на них выдавать можно —
 * список модулей приходит с сервера (R-70).
 */
const EXTRA_NAMES: Record<string, string> = {
  audit: 'Журнал действий',
  translations: 'Переводы',
}

export function moduleName(module: string): string {
  return MODULES.find((m) => m.id === module)?.name ?? EXTRA_NAMES[module] ?? module
}

/** Разделы, открытые роли хотя бы на чтение. */
export const openModules = (role: Role, modules: string[]) =>
  modules.filter((module) => rolePermission(role, module).includes('r'))

/**
 * Переключение одного права. Включая что угодно, включаем и чтение; снимая
 * чтение, закрываем раздел целиком — право «менять то, чего не видишь» не
 * существует.
 */
export function togglePermission(role: Role, module: string, letter: PermissionLetter): Role {
  const current = rolePermission(role, module)
  const has = current.includes(letter)
  let next: string
  if (letter === 'r') {
    next = has ? '' : normalize(`${current}r`)
  } else {
    next = has ? normalize(current.replace(letter, '')) : normalize(`${current}${letter}r`)
  }
  return { ...role, perms: { ...role.perms, [module]: next } }
}

/** Открыть или закрыть роли всё разом. */
export function setAllPermissions(role: Role, modules: string[], open: boolean): Role {
  const perms: Record<string, string> = {}
  modules.forEach((module) => {
    perms[module] = open ? 'crud' : ''
  })
  return { ...role, perms }
}
